/**
 * @fileoverview Product Inventory Manager Web Component
 * @description Centralized inventory tracking for all product variants.
 * Fetches real-time inventory from Shopify Storefront API and broadcasts
 * updates to bundle items via custom events.
 * @requires Shopify Storefront API token configured in theme settings
 */

/** @const {Object} Configuration constants for inventory management */
const INVENTORY_CONSTANTS = {
  // Timing
  CART_UPDATE_DELAY: 300, // ms to wait after cart operation
  POLLING_INTERVAL: 30000, // 30 seconds
  FOCUS_DEBOUNCE_DELAY: 100, // ms

  // Fuzzy inventory thresholds
  FUZZY_THRESHOLD_LOW: 50,
  FUZZY_THRESHOLD_MID: 100,
  FUZZY_VALUE_MID: 75,
  FUZZY_VALUE_HIGH: 100,

  // Events
  INVENTORY_UPDATE_EVENT: 'product-inventory-update',
  INVENTORY_READY_EVENT: 'inventory-manager-ready',
  INVENTORY_ERROR_EVENT: 'inventory-manager-error',
  SELECTION_INVALID_EVENT: 'bundle-selection-invalid',

  // DOM Selectors
  BUNDLE_TITLE_SELECTOR: '.bundles__title-wrapper',
  BUNDLE_ITEM_SELECTOR: 'bundle-item',
  INVENTORY_MANAGER_SELECTOR: 'product-inventory-manager',

  // API
  GRAPHQL_VERSION: '2024-01',
  CART_ENDPOINT: '/cart.js'
};

/**
 * Custom element for managing product inventory across variants
 * @extends HTMLElement
 */
class ProductInventoryManager extends HTMLElement {
  /**
   * Initialize inventory manager
   */
  constructor() {
    super();
    /** @type {Map<string, Object>} Inventory data by variant ID */
    this.inventoryData = new Map();
    /** @type {Map<string, number>} Cart quantities by variant ID */
    this.cartData = new Map();
    /** @type {number|null} Polling interval ID */
    this.pollingInterval = null;
    /** @type {boolean} Initialization state */
    this.isInitialized = false;
  }

  /**
   * Web component lifecycle - element added to DOM
   */
  connectedCallback() {
    this.productId = this.getAttribute('data-product-id');
    this.variantIds = this.getAttribute('data-variant-ids')?.split(',').filter(Boolean) || [];

    if (this.productId && this.variantIds.length > 0) {
      this._interceptCartAdds();
      this._setupCartListeners();
      this._startInventoryTracking();

      // Listen for invalid selections and auto-select best alternative
      document.addEventListener(INVENTORY_CONSTANTS.SELECTION_INVALID_EVENT, () => {
        // Find all visible non-custom bundle items
        // data-number="0" indicates custom bundle (user picks quantity)
        // data-number="1", "3", "6" etc are fixed bundle quantities
        // The :not([data-number="0"]) selector excludes the custom option
        const visibleBundles = Array.from(
          document.querySelectorAll(`${INVENTORY_CONSTANTS.BUNDLE_ITEM_SELECTOR}[data-number]:not([data-number="0"])`)
        ).filter(item => item.style.display !== 'none');

        if (visibleBundles.length > 0) {
          // Select the last one (highest quantity before custom)
          visibleBundles[visibleBundles.length - 1].click();
        } else {
          // If no bundles available, select single option
          const singleOption = document.querySelector(`${INVENTORY_CONSTANTS.BUNDLE_ITEM_SELECTOR}[data-number="1"]`);
          if (singleOption) singleOption.click();
        }
      });
    }
  }

  /**
   * Web component lifecycle - element removed from DOM
   */
  disconnectedCallback() {
    this._stopPolling();
  }

  /**
   * Initialize inventory tracking system
   * @private
   * @async
   * @returns {Promise<void>}
   */
  async _startInventoryTracking() {
    try {
      // Initial fetch - wait for both to complete
      await Promise.all([
        this._fetchAllInventory(false), // Don't broadcast
        this._fetchCartQuantities(false) // Don't broadcast
      ]);

      // Broadcast once after both complete
      this._broadcastInventoryUpdate();

      this.isInitialized = true;
      this._startPolling();
      this._broadcastInitialized();
    } catch (error) {
      console.error('Failed to start inventory tracking:', error);
    }
  }

  /**
   * Intercept cart operations to trigger inventory updates
   * @private
   */
  _interceptCartAdds() {
    // Only intercept once
    if (window._cartInterceptorInstalled) return;
    window._cartInterceptorInstalled = true;

    // Store original fetch
    const originalFetch = window.fetch;

    // Override fetch to detect cart operations
    window.fetch = (...args) => {
      const url = args[0];

      // Check if this is a cart operation
      if (url && (url.includes('/cart/add') || url.includes('/cart/update') || url.includes('/cart/change'))) {
        // Call original fetch
        const promise = originalFetch.apply(window, args);

        // Update cart quantities after cart operation completes
        promise
          .then(() => {
            // Short delay to ensure cart is updated
            setTimeout(() => {
              // Find all inventory managers and update them
              document.querySelectorAll(INVENTORY_CONSTANTS.INVENTORY_MANAGER_SELECTOR).forEach(manager => {
                if (manager._fetchCartQuantities) {
                  manager._fetchCartQuantities(true);
                }
              });
            }, INVENTORY_CONSTANTS.CART_UPDATE_DELAY);
          })
          .catch(() => {
            // Even on error, check cart state
            setTimeout(() => {
              document.querySelectorAll(INVENTORY_CONSTANTS.INVENTORY_MANAGER_SELECTOR).forEach(manager => {
                if (manager._fetchCartQuantities) {
                  manager._fetchCartQuantities(true);
                }
              });
            }, INVENTORY_CONSTANTS.CART_UPDATE_DELAY);
          });

        return promise;
      }

      // Not a cart operation, proceed normally
      return originalFetch.apply(window, args);
    };
  }

  /**
   * Fetch inventory data for all tracked variants
   * @private
   * @async
   * @param {boolean} [broadcast=true] - Whether to broadcast updates
   * @returns {Promise<void>}
   */
  async _fetchAllInventory(broadcast = true) {
    try {
      const token = window.theme?.settings?.storefront_api_token;
      if (!token) {
        console.warn('Storefront API token not configured');
        return;
      }

      // Ultra-efficient query: fetch ONLY quantityAvailable for specific variants
      const variantNodes = this.variantIds
        .map(
          id =>
            `v${id}: node(id: "gid://shopify/ProductVariant/${id}") {
          ... on ProductVariant {
            quantityAvailable
          }
        }`
        )
        .join('\n');

      const query = `{
        ${variantNodes}
      }`;

      const response = await fetch(
        `https://${window.Shopify.shop}/api/${INVENTORY_CONSTANTS.GRAPHQL_VERSION}/graphql.json`,
        {
          method: 'POST',
          headers: {
            'X-Shopify-Storefront-Access-Token': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query })
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const responseText = await response.text();
      const { data } = JSON.parse(responseText);

      // Clear and update inventory data
      this.inventoryData.clear();

      // Parse the simplified response - each variant is a direct property
      this.variantIds.forEach(variantId => {
        const variantData = data[`v${variantId}`];
        if (variantData) {
          const quantity = variantData.quantityAvailable || 0;
          this.inventoryData.set(variantId, {
            available: quantity > 0, // If quantity > 0, it's available
            quantity: this._fuzzyInventory(quantity)
          });
        }
      });

      // Only broadcast if requested
      if (broadcast) {
        this._broadcastInventoryUpdate();
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      this._broadcastError(error);
    }
  }

  /**
   * Fetch current cart quantities for tracked variants
   * @private
   * @async
   * @param {boolean} [broadcast=true] - Whether to broadcast updates
   * @returns {Promise<void>}
   */
  async _fetchCartQuantities(broadcast = true) {
    try {
      const response = await fetch(INVENTORY_CONSTANTS.CART_ENDPOINT);
      const cart = await response.json();

      // Clear previous cart data
      this.cartData.clear();

      // Update cart quantities for tracked variants
      cart.items.forEach(item => {
        const variantId = item.variant_id.toString();
        if (this.variantIds.includes(variantId)) {
          this.cartData.set(variantId, item.quantity);
        }
      });

      // Only broadcast if requested
      if (broadcast) {
        this._broadcastInventoryUpdate();
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  /**
   * Apply fuzzing to inventory quantities for security
   * @private
   * @param {number} actualQuantity - Actual inventory quantity
   * @returns {number} Fuzzed inventory quantity
   */
  _fuzzyInventory(actualQuantity) {
    if (actualQuantity === 0) return 0;
    if (actualQuantity <= INVENTORY_CONSTANTS.FUZZY_THRESHOLD_LOW) return actualQuantity;
    if (actualQuantity <= INVENTORY_CONSTANTS.FUZZY_THRESHOLD_MID) return INVENTORY_CONSTANTS.FUZZY_VALUE_MID;
    return INVENTORY_CONSTANTS.FUZZY_VALUE_HIGH;
  }

  /**
   * Broadcast inventory updates to all listening components
   * @private
   */
  _broadcastInventoryUpdate() {
    const hasInventory = Array.from(this.inventoryData.values()).some(inv => inv.quantity > 0);

    if (!hasInventory) {
      // Hide all bundles, show Klaviyo, and return early
      this._checkZeroInventory();
      return; // Skip all the event broadcasting
    }

    // Normal broadcasting for when we have inventory
    this.variantIds.forEach(variantId => {
      const inventory = this.inventoryData.get(variantId) || { quantity: 0, available: false };
      const cartQuantity = this.cartData.get(variantId) || 0;
      const maxAvailable = Math.max(0, inventory.quantity - cartQuantity);

      const event = new CustomEvent(INVENTORY_CONSTANTS.INVENTORY_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          variantId: variantId,
          inventoryQuantity: inventory.quantity,
          cartQuantity: cartQuantity,
          maxAvailable: maxAvailable,
          available: inventory.available
        }
      });

      document.dispatchEvent(event);
    });
  }

  /**
   * Broadcast initialization complete event
   * @private
   */
  _broadcastInitialized() {
    const event = new CustomEvent(INVENTORY_CONSTANTS.INVENTORY_READY_EVENT, {
      bubbles: true,
      detail: { productId: this.productId }
    });
    document.dispatchEvent(event);
  }

  /**
   * Broadcast error event
   * @private
   * @param {Error} error - Error object to broadcast
   */
  _broadcastError(error) {
    const event = new CustomEvent(INVENTORY_CONSTANTS.INVENTORY_ERROR_EVENT, {
      bubbles: true,
      detail: { error: error.message }
    });
    document.dispatchEvent(event);
  }

  /**
   * Handle zero inventory state
   * @private
   */
  _checkZeroInventory() {
    const hasInventory = Array.from(this.inventoryData.values()).some(inv => inv.quantity > 0);
    const bundleItems = document.querySelectorAll(INVENTORY_CONSTANTS.BUNDLE_ITEM_SELECTOR);

    bundleItems.forEach(item => {
      item.style.display = hasInventory ? '' : 'none';
    });

    // Hide/show the bundle title when out of stock
    const bundleTitle = document.querySelector(INVENTORY_CONSTANTS.BUNDLE_TITLE_SELECTOR);
    if (bundleTitle) {
      bundleTitle.style.display = hasInventory ? '' : 'none';
    }
  }

  /**
   * Start periodic inventory polling
   * @private
   */
  _startPolling() {
    this.pollingInterval = setInterval(async () => {
      await Promise.all([this._fetchAllInventory(false), this._fetchCartQuantities(false)]);
      this._broadcastInventoryUpdate(); // Broadcast once after both complete
    }, INVENTORY_CONSTANTS.POLLING_INTERVAL);
  }

  /**
   * Stop periodic inventory polling
   * @private
   */
  _stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Set up event listeners for cart and page visibility changes
   * @private
   */
  _setupCartListeners() {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // User just returned to the tab
        Promise.all([this._fetchAllInventory(false), this._fetchCartQuantities(false)]).then(() => {
          this._broadcastInventoryUpdate();
        });
      }
    });

    // Also refresh when window gains focus (handles some edge cases)
    window.addEventListener('focus', () => {
      // Debounce to avoid multiple refreshes
      if (this._focusTimeout) clearTimeout(this._focusTimeout);
      this._focusTimeout = setTimeout(() => {
        Promise.all([this._fetchAllInventory(false), this._fetchCartQuantities(false)]).then(() => {
          this._broadcastInventoryUpdate();
        });
      }, INVENTORY_CONSTANTS.FOCUS_DEBOUNCE_DELAY);
    });
  }

  /**
   * Force refresh inventory and cart data
   * @public
   * @returns {Promise<void>}
   */
  refresh() {
    return Promise.all([
      this._fetchAllInventory(false), // Don't broadcast
      this._fetchCartQuantities(false) // Don't broadcast
    ]).then(() => {
      this._broadcastInventoryUpdate(); // Broadcast once after both
    });
  }
}

// Register the custom element
if (!customElements.get('product-inventory-manager')) {
  customElements.define('product-inventory-manager', ProductInventoryManager);
}
